<template>
  <div class="procedures ms-container">
    <div class="procedures__header filters-bar">
      <procedures-filter />
      <i-button
        v-if="getCommonModulePerms('procedure', 'create')"
        type="success"
        @click="createProcedure"
      >
        Создать {{ this.object === 'expertise' ? 'заключение' : 'отзыв' }}
      </i-button>
    </div>

    <module-header>
      {{ this.object === 'expertise' ? 'Заключения экспертизы' : 'Отзывы' }}
    </module-header>

    <div class="procedures__container">
      <inline-loader :loading="loading" v-if="loading && !nodata" />
      <div class="ms-nodata ms-text" v-else-if="!loading && nodata">
        Не удалось загрузить список запросов. Повторите, пожалуйста, попытку позже
      </div>
      <procedures-list
        :procedures="procedures"
        @open-procedure="openProcedure"
        v-else-if="procedures.length"
        :object="object"
      />
      <div v-else class="ms-nodata ms-text">
        Список пуст
      </div>
    </div>

    <modal
      v-if="!loading"
      class-name="procedures-editor editor-modal"
      v-model="procedureModal"
      :width="procedure.id ? 840 : 420"
      :styles="{ top: '40px' }"
      :mask-closable="false"
    >
      <procedures-editor-header slot="header" :procedure="procedure" />
      <div class="procedures-editor__container">
        <alert
          v-if="editorState.warning"
          class="alerts-item"
          type="warning"
        >
          {{ editorState.warning }}
        </alert>
        <row :gutter="16">
          <i-col
            :span="procedure.id ? '12' : '24'"
            class="procedures-editor__content"
          >
            <normal-card>
              <procedures-editor-contract :contract="contract" />
              <procedures-editor-info
                :procedure="procedure"
                :procedure-statuses="procedureStatuses"
                :procedure-types="procedureTypes"
                :object="object"
              />
              <resources-editor
                @update="getComments(true)"
                :type="`${object}s`"
                :parent="procedure"
              />
            </normal-card>
          </i-col>
          <i-col v-if="procedure.id" span="12">
            <procedures-editor-history
              :comments="comments"
              :loading="commentsLoading"
            />
            <comments-add-form
              v-if="procedure.id"
              :id="procedure.id"
              type="procedures"
              @update="getComments(true)"
            />
          </i-col>
        </row>
      </div>
      <div slot="footer" class="procedures-editor__footer">
        <i-button
          :disabled="!procedure.id || !getCommonModulePerms('procedure', 'delete')"
          type="error"
          size="default"
          :loading="deleting"
          @click="deleteProcedure"
        >
          Удалить
        </i-button>
        <i-button
          type="success"
          size="default"
          :loading="saving"
          :disabled="editorState.disabled || !getCommonModulePerms('procedure', 'update')"
          @click="saveProcedure"
        >
          Сохранить
        </i-button>
      </div>
    </modal>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep'
import moment from 'moment'
import ProceduresList from './procedures-list'
import ProceduresFilter from '@/components/filters/procedures'
import InlineLoader from '@/components/loaders/inline-loader'
import NormalCard from '@/components/parts/normal-card'
import ModuleHeader from '@/components/parts/module-header'
import ProceduresEditorHeader from './editor/procedures-editor-header'
import ProceduresEditorHistory from './editor/procedures-editor-history'
import ProceduresEditorContract from './editor/procedures-editor-contract'
import ProceduresEditorInfo from './editor/procedures-editor-info'
import ResourcesEditor from '@/components/resources/editor/resources-editor-simple'
import CommentsAddForm from '@/components/comments/comments-add-form'
import { procedure, procedureStage } from '@/config/procedures'
import { getProceduresUrl } from '@/config/filters'
import { formatDefaultDate } from '@/utils'

export default {
  props: {
    object: {
      type: String,
      default: 'conclusion'
    }
  },
  components: {
    ProceduresList,
    ProceduresFilter,
    InlineLoader,
    NormalCard,
    ModuleHeader,
    ProceduresEditorHeader,
    ProceduresEditorHistory,
    ProceduresEditorContract,
    ProceduresEditorInfo,
    ResourcesEditor,
    CommentsAddForm
  },
  data () {
    return {
      loading: false,
      nodata: false,
      contract: null,
      procedureStatuses: [],
      procedureTypes: [],
      procedures: [],
      comments: [],
      commentsLoading: false,
      procedureModal: false,
      deleting: false,
      saving: false,
      procedure
    }
  },
  computed: {
    filteredStages () {
      return this.procedure.procedure_stages.filter((s) => !s.disabled)
    },
    editorState () {
      const {
        object_resources,
        description
      } = this.procedure
      const procedure_status_id = this.procedure[`${this.object}_status_id`]

      if (!procedure_status_id) {
        return {
          disabled: true,
          warning: 'Необходимо указать текущий статус запроса'
        }
      }

      // check documents
      const status = this.procedureStatuses.find((s) => s.id === procedure_status_id)

      if (status.name === 'Получен' && !description) {
        return {
          disabled: true,
          warning:
            'Необходимо указать реквизиты документа, если статус запроса "Получен"'
        }
      }

      if (status.name === 'Получен' && object_resources && !object_resources.length) {
        return {
          disabled: true,
          warning:
            'Необходимо приложить документы к запросу, если статус запроса "Получен"'
        }
      }

      return {
        disabled: false,
        warning: null
      }
    }
  },
  watch: {
    '$store.state.filters.procedures': {
      handler: function () {
        this.getProcedures()
      },
      deep: true
    }
    // '$store.state.socket': {
    //   handler: function (val) {
    //     if (!val.create && !val.update && !val.delete) return
    //     const { type } = this.$store.state.socket

    //     if (type === `${this.object}s`) {
    //       this.getProcedures()
    //     }

    //     this.$store.commit('CLEAR_SOCKET_STATE')
    //   },
    //   deep: true,
    //   immediate: true
    // }
  },
  methods: {
    async openProcedure ({ id }) {
      try {
        const url = `page/${this.object}/${id}`
        const procedure = await this.$store.dispatch('GET_REQUEST', { url })

        if (procedure.creation_date) { procedure.creation_date = formatDefaultDate(procedure.creation_date) }
        if (procedure.receive_date) { procedure.receive_date = formatDefaultDate(procedure.receive_date) }

        this.procedure = procedure
        this.procedureModal = true
        await this.getComments()
      } catch (error) {
        console.log(error)
      }
    },
    async getComments (noloading) {
      try {
        if (!noloading) this.commentsLoading = true
        const url = `page/comment?object_type=procedures&object_id=${this.procedure.id}`
        this.comments = await this.$store.dispatch('GET_REQUEST', { url })
        this.commentsLoading = false
      } catch (error) {
        console.log(error)
        this.commentsLoading = false
      }
    },
    createProcedure () {
      const status = this.procedureStatuses.find(
        (s) => s.name === 'Отправлен запрос'
      )
      this.$set(this, 'procedure', cloneDeep(procedure))
      this.$set(this.procedure, 'id', null)
      if (status) this.procedure[`${this.object}_status_id`] = status.id
      this.procedure.creation_date = new Date()
      this.procedure.receive_date = new Date(moment().add(30, 'days'))

      this.procedureModal = true
    },
    addProcedureStage () {
      this.procedure.procedure_stages.push({ ...procedureStage })
    },
    deleteProcedureStage ({ index, id }) {
      if (!id) this.procedure.procedure_stages.splice(index, 1)
      else this.$set(this.procedure.procedure_stages[index], 'disabled', true)
    },
    async getProcedures () {
      const { id } = this.$route.params

      try {
        const url = getProceduresUrl(
          `page/${this.object}?contract_id=${id}`,
          this.$store.state.filters.procedures
        )
        this.procedures = await this.$store.dispatch('GET_REQUEST', { url })
      } catch (error) {
        console.log(error)
      }
    },
    async getData () {
      const { id } = this.$route.params

      try {
        this.loading = true
        const contractUrl = `page/project/${id}`
        const procedureStatusesUrl = `objectInfo/${this.object}_statuses`
        const procedureTypesUrl = `objectInfo/${this.object}_types`

        this.contract = await this.$store.dispatch('GET_REQUEST', {
          url: contractUrl
        })
        this.procedureStatuses = await this.$store.dispatch('GET_REQUEST', {
          url: procedureStatusesUrl
        })
        this.procedureTypes = await this.$store.dispatch('GET_REQUEST', {
          url: procedureTypesUrl
        })
        await this.getProcedures()

        this.loading = false
      } catch (error) {
        console.log(error)
        this.loading = false
        this.nodata = true
      }
    },
    getProcedureData () {
      const {
        id,
        name,
        description,
        object_resources,
        procedure_stages
      } = this.procedure

      const data = {
        id,
        name,
        description,
        object_resources,
        procedure_stages,
        contract_id: this.contract.id
      }

      data[`${this.object}_status_id`] = this.procedure[`${this.object}_status_id`]

      if (this.object === 'expertise') {
        data.expertise_type_id = this.procedure.expertise_type_id
      }

      return data
    },
    async deleteProcedure () {
      try {
        this.deleting = true
        const url = `page/${this.object}?id=${this.procedure.id}`
        await this.$store.dispatch('DELETE_REQUEST', { url })

        this.getProcedures()

        this.$Message.success('Удаление данных выполнено успешно')
        this.deleting = false
        this.procedureModal = false
      } catch (error) {
        console.log(error)
        this.$Message.error('Произошла ошибка при удалении данных')
        this.deleting = false
      }
    },
    async saveProcedure () {
      const { id } = this.$route.params

      try {
        this.saving = true
        const url = `page/${this.object}?contract_id=${id}`
        const data = this.getProcedureData()
        await this.$store.dispatch('POST_REQUEST', { url, data })

        this.getProcedures()

        this.$set(this, 'procedure', cloneDeep(procedure))
        this.$Message.success('Сохранение данных выполнено успешно')
        this.saving = false
        this.procedureModal = false
      } catch (error) {
        console.log(error)
        this.$Message.error('Произошла ошибка при сохранении данных')
        this.saving = false
      }
    }
  },
  async created () {
    await this.getData()

    const { open } = this.$route.query
    if (open) this.openProcedure({ id: open })
  }
}
</script>

<style lang="less">
.procedures-editor {
  &__content {
    max-height: 70vh;
    overflow: auto;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
  }

  .comments-add-form__message {
    flex: 1;
  }
  .comments-add-form__textarea {
    width: 100% !important;
  }
}
</style>
